import React, { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { Feature, Point } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import './App.css';
import './App.mobile.css';
import './components/EnhancedJobPopup.css';
import './components/SalaryMap.css';
import './config/mapbox.config'; // Import to set the access token

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Companies from './components/Companies';
import Salaries from './components/Salaries';
import LandingPage from './components/LandingPage';
import CompanyDetailsPanel from './components/CompanyDetailsPanel';
import MobileNav from './components/MobileNav';
import MobileMapControls from './components/MobileMapControls';
import MobileFilterSheet from './components/MobileFilterSheet';
import LoginModal from './components/Auth/LoginModal';
import ProfileSettings from './components/Profile/ProfileSettings';
import ReferralDashboard from './components/ReferralDashboard/ReferralDashboard';
import Footer from './components/Footer';
import ComparisonCartWidget from './components/ComparisonCartWidget';
import ComparisonView from './components/ComparisonView';

// Context providers
import { MapProvider } from './contexts/MapContext';
import { AuthProvider } from './contexts/AuthContext';
import { ComparisonProvider } from './contexts/ComparisonContext';

// Utils
import './utils/clearComparisonData';

// Services
import { savedJobsService } from './services/savedJobs.service';
import { savedCompaniesService } from './services/savedCompanies.service';
import { referralsService } from './services/referrals.service';
import { sessionService } from './services/session.service';

// Types
import { JobFilters as JobFiltersType, Job } from './types/JobTypes';
import { CompanyGeoJSON, Company } from './types/CompanyTypes';

// Mock Data
import { generateMockJobData, mockCompanyData } from './data/mockData';

// Mapbox access token is now set in mapbox.config.ts

function App() {
  // Core state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [activePage, setActivePage] = useState<string>('landing');
  const [profileTab, setProfileTab] = useState<'profile' | 'resume' | 'saved-jobs' | 'saved-companies' | 'referrals' | 'preferences' | 'privacy'>('profile');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState<boolean>(false);
  
  // Data state - Generate comprehensive mock data
  const [jobMarketData, setJobMarketData] = useState<any>(generateMockJobData());
  
  const [companyData, setCompanyData] = useState<any>(mockCompanyData);
  
  // Filters state
  const [activeFilters, setActiveFilters] = useState<JobFiltersType>({
    search: '',
    countries: [],
    industries: [],
    jobTypes: [],
    positions: [],
    salary: [0, 200000]
  });
  
  // State for filtered jobs
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  
  // State for company filters
  const [companyFilters, setCompanyFilters] = useState<{
    searchTerm?: string;
    countries?: string[];
  }>({});
  
  // State for selected location (for cost of living display)
  const [selectedLocation, setSelectedLocation] = useState<{
    city: string;
    country: string;
    costOfLiving: number;
    averageSalary: number;
  } | null>(null);
  
  // State for selected company (for company details panel)
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  
  // State for map instance to properly track it
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);
  
  // Login modal state
  const [isLoginModalOpen, setIsLoginModalOpen] = useState<boolean>(false);
  const [loginModalAction, setLoginModalAction] = useState<'apply' | 'save' | 'share' | 'compare-salary' | 'upload-resume' | string>('apply');
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'signup'>('login');
  
  // Referral dashboard state
  const [referralDashboard, setReferralDashboard] = useState<{ companyId: string; companyName: string } | null>(null);
  
  // Comparison modal state
  const [comparisonModalType, setComparisonModalType] = useState<'country' | 'company' | 'job' | null>(null);
  
  // Saved jobs state
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [savedCompanyIds, setSavedCompanyIds] = useState<Set<string>>(new Set());
  
  // Fullscreen company state
  const [fullscreenCompany, setFullscreenCompany] = useState<any>(null);
  
  // Export sessionService globally for auth service
  useEffect(() => {
    (window as any).sessionService = sessionService;
  }, []);
  
  // Load saved items on mount
  useEffect(() => {
    const loadSavedItems = async () => {
      const userId = sessionService.getUserId();
      if (!userId) return;
      
      try {
        // Load saved jobs
        const savedJobs = await savedJobsService.getSavedJobs(userId);
        const jobIds = new Set(savedJobs.map(job => job.id));
        setSavedJobIds(jobIds);
        
        // Load saved companies
        const savedCompanies = await savedCompaniesService.getSavedCompanies(userId);
        const companyIds = new Set(savedCompanies.map(company => company.id));
        setSavedCompanyIds(companyIds);
      } catch (error) {
        console.error('Error loading saved items:', error);
      }
    };
    
    loadSavedItems();
  }, []);
  
  // Function to show job details popup
  const handleJobClick = (job: any) => {
    // Center map on job location
    if (map.current && job.coordinates) {
      map.current.flyTo({
        center: job.coordinates,
        zoom: 14,
        duration: 1500
      });
    }
    
    // Open job in fullscreen
    showJobDetailsPopup(job);
  };

  const showJobDetailsPopup = (jobData: any) => {
    // Extract properties from jobData
    const { 
      id, title, salary, company, employmentType, remote, location, coordinates,
      companyRating, openPositions, benefits, yearsOfExperience, description,
      industry, country, city, state
    } = jobData;
    
    // Calculate regional salary comparison (mock data - would come from API)
    const avgRegionalSalary = salary ? Number(salary) * 0.95 : 75000;
    const salaryPercentile = salary ? Math.min(95, Math.max(5, 50 + ((Number(salary) - avgRegionalSalary) / avgRegionalSalary) * 50)) : 50;
    
    // Format salary
    const formattedSalary = salary ? `$${Number(salary).toLocaleString()}` : 'Not disclosed';
    
    // Format rating
    const rating = companyRating ? Number(companyRating).toFixed(1) : '4.2';
    
    // Create full-screen popup overlay
    const fullscreenDiv = document.createElement('div');
    fullscreenDiv.className = 'job-fullscreen-overlay';
    fullscreenDiv.innerHTML = `
      <div class="job-fullscreen-popup ${darkMode ? 'dark' : ''}">
        <div class="fullscreen-overlay">
          <div class="fullscreen-content">
            <button class="fullscreen-close-btn" onclick="this.closest('.job-fullscreen-overlay').remove()">×</button>
            
            <div class="fullscreen-header">
              <div class="job-main-info">
                <h1>${title || 'Job Title'}</h1>
                <div class="company-info-large">
                  <span class="company-name-large">${company || 'Company'}</span>
                  <div class="company-rating-badge">
                    <span class="star">★</span>
                    <span>${rating}</span>
                  </div>
                </div>
                <div class="location-large">${location}</div>
                <p class="job-description-large">${description || 'Join our team in this exciting role where you will contribute to innovative projects and work with cutting-edge technologies.'}</p>
              </div>
              <div class="salary-showcase">
                <div class="salary-amount-large">${formattedSalary}</div>
                <div class="salary-label-large">Annual Salary</div>
                <div class="salary-percentile-large">
                  <div class="percentile-bar-large">
                    <div class="percentile-fill-large" style="width: ${salaryPercentile}%"></div>
                    <div class="percentile-marker-large" style="left: ${salaryPercentile}%"></div>
                  </div>
                  <div class="percentile-label-large">Top ${100 - Math.round(salaryPercentile)}% in ${city || location}</div>
                </div>
              </div>
            </div>
            
            <div class="fullscreen-body">
              <div class="left-column">
                <div class="job-details-section">
                  <h3>Job Details</h3>
                  <div class="job-meta-large">
                    <div class="meta-item-large">${employmentType || 'Full-time'}</div>
                    ${remote ? '<div class="meta-item-large remote">Remote</div>' : ''}
                    <div class="meta-item-large">${industry || 'Technology'}</div>
                    ${yearsOfExperience ? `<div class="meta-item-large">${yearsOfExperience}+ years experience</div>` : ''}
                  </div>
                </div>
                
                <div class="company-metrics-section">
                  <h3>Company Ratings</h3>
                  <div class="metrics-grid-large">
                    <div class="metric-card-large">
                      <div class="metric-header-large">
                        <span class="metric-title-large">Overall Rating</span>
                        <span class="metric-score-large">${rating}/5</span>
                      </div>
                      <div class="metric-bar-large">
                        <div class="fill-large" style="width: ${(Number(rating) / 5) * 100}%"></div>
                      </div>
                    </div>
                    <div class="metric-card-large">
                      <div class="metric-header-large">
                        <span class="metric-title-large">Work-Life Balance</span>
                        <span class="metric-score-large">4.1/5</span>
                      </div>
                      <div class="metric-bar-large">
                        <div class="fill-large" style="width: 82%"></div>
                      </div>
                    </div>
                    <div class="metric-card-large">
                      <div class="metric-header-large">
                        <span class="metric-title-large">Compensation</span>
                        <span class="metric-score-large">4.3/5</span>
                      </div>
                      <div class="metric-bar-large">
                        <div class="fill-large" style="width: 86%"></div>
                      </div>
                    </div>
                    <div class="metric-card-large">
                      <div class="metric-header-large">
                        <span class="metric-title-large">Career Growth</span>
                        <span class="metric-score-large">3.9/5</span>
                      </div>
                      <div class="metric-bar-large">
                        <div class="fill-large" style="width: 78%"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="right-column">
                <div class="salary-comparison-large">
                  <h3>Salary Comparison</h3>
                  <div class="comparison-section">
                    <h4>Regional Comparison - ${location}</h4>
                    <div class="comparison-grid-large">
                      <div class="comparison-item-large">
                        <span class="comparison-label-large">This Position</span>
                        <span class="comparison-value-large highlight">${formattedSalary}</span>
                      </div>
                      <div class="comparison-item-large">
                        <span class="comparison-label-large">Average ${title || 'Similar Role'}</span>
                        <span class="comparison-value-large">$${avgRegionalSalary.toLocaleString()}</span>
                      </div>
                      <div class="comparison-item-large">
                        <span class="comparison-label-large">Entry Level</span>
                        <span class="comparison-value-large">$${(avgRegionalSalary * 0.7).toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}</span>
                      </div>
                      <div class="comparison-item-large">
                        <span class="comparison-label-large">Senior Level</span>
                        <span class="comparison-value-large">$${(avgRegionalSalary * 1.4).toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="country-comparison-large">
                    <h4>Compare with Another Country</h4>
                    <div class="comparison-input-large">
                      <input type="text" id="country-compare-${id || 'job'}" placeholder="Enter country name (e.g., Germany, Poland, UK)" class="country-input-large">
                      <button class="compare-btn-large" onclick="compareJobWithCountry('${id || 'job'}', '${title}', ${salary || 0})">Compare</button>
                    </div>
                    <div id="country-comparison-result-${id || 'job'}" class="comparison-result-large"></div>
                  </div>
                </div>
                
                <div class="benefits-section-large">
                  <h3>Benefits & Perks</h3>
                  <div class="benefits-grid-large">
                    ${benefits ? 
                      (() => {
                        try {
                          // If benefits is an object or JSON string, parse it
                          const benefitsObj = typeof benefits === 'string' ? JSON.parse(benefits) : benefits;
                          if (typeof benefitsObj === 'object' && benefitsObj !== null) {
                            return Object.entries(benefitsObj).map(([key, value]) => 
                              `<div class="benefit-item-large ${value ? 'included' : 'not-included'}">
                                ${value ? '✓' : '✗'} ${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </div>`
                            ).join('');
                          } else {
                            // If it's a simple string, split by comma
                            return benefits.toString().split(',').map((benefit: string) => 
                              `<div class="benefit-item-large included">✓ ${benefit.trim()}</div>`
                            ).join('');
                          }
                        } catch (e) {
                          // If parsing fails, treat as comma-separated string
                          return benefits.toString().split(',').map((benefit: string) => 
                            `<div class="benefit-item-large included">✓ ${benefit.trim()}</div>`
                          ).join('');
                        }
                      })()
                    : '<div class="benefit-item-large">Benefits information not available</div>'}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Income Breakdown Section -->
            <div class="sankey-section">
              <h3>Income Breakdown</h3>
              <div class="income-breakdown-compact">
                <div class="income-flow-section">
                  <h4>Annual Income Flow</h4>
                  <div class="income-flow-grid">
                    <div class="income-flow-item">
                      <span class="income-icon material-icons">account_balance_wallet</span>
                      <span class="income-label">Gross Salary</span>
                      <span class="income-value">${formattedSalary}</span>
                      <div class="income-bar">
                        <div class="income-fill" style="width: 100%; background-color: rgb(31, 119, 180);"></div>
                      </div>
                    </div>
                    <div class="income-flow-item deduction">
                      <span class="income-icon material-icons">receipt_long</span>
                      <span class="income-label">Income Tax</span>
                      <span class="income-value">-$${salary ? Math.round(Number(salary) * 0.22).toLocaleString() : '0'}</span>
                      <div class="income-bar">
                        <div class="income-fill" style="width: 22%; background-color: rgb(255, 127, 14);"></div>
                      </div>
                    </div>
                    <div class="income-flow-item deduction">
                      <span class="income-icon material-icons">security</span>
                      <span class="income-label">Social Security</span>
                      <span class="income-value">-$${salary ? Math.round(Number(salary) * 0.08).toLocaleString() : '0'}</span>
                      <div class="income-bar">
                        <div class="income-fill" style="width: 8%; background-color: rgb(44, 160, 44);"></div>
                      </div>
                    </div>
                    <div class="income-flow-item net">
                      <span class="income-icon material-icons">savings</span>
                      <span class="income-label">Net Income</span>
                      <span class="income-value">$${salary ? Math.round(Number(salary) * 0.7).toLocaleString() : '0'}</span>
                      <div class="income-bar">
                        <div class="income-fill" style="width: 70%; background-color: rgb(214, 39, 40);"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="monthly-breakdown-section">
                  <h4>Monthly Expense Distribution</h4>
                  <svg class="expense-breakdown-chart" viewBox="0 0 600 200" preserveAspectRatio="xMidYMid meet">
                    <rect x="0" y="0" rx="4" ry="4" width="140" height="25" fill="#9467bd" fill-opacity="1" stroke-width="0">
                      <title>Rent: $${salary ? Math.round(Number(salary) * 0.7 * 0.35 / 12).toLocaleString() : '0'} (35%)</title>
                    </rect>
                    <text x="145" y="17" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Rent: $${salary ? Math.round(Number(salary) * 0.7 * 0.35 / 12).toLocaleString() : '0'} (35%)</text>
                    <rect x="0" y="30" rx="4" ry="4" width="60" height="25" fill="#8c564b" fill-opacity="1" stroke-width="0">
                      <title>Food: $${salary ? Math.round(Number(salary) * 0.7 * 0.15 / 12).toLocaleString() : '0'} (15%)</title>
                    </rect>
                    <text x="65" y="47" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Food: $${salary ? Math.round(Number(salary) * 0.7 * 0.15 / 12).toLocaleString() : '0'} (15%)</text>
                    <rect x="0" y="60" rx="4" ry="4" width="40" height="25" fill="#e377c2" fill-opacity="1" stroke-width="0">
                      <title>Transport: $${salary ? Math.round(Number(salary) * 0.7 * 0.10 / 12).toLocaleString() : '0'} (10%)</title>
                    </rect>
                    <text x="45" y="77" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Transport: $${salary ? Math.round(Number(salary) * 0.7 * 0.10 / 12).toLocaleString() : '0'} (10%)</text>
                    <rect x="0" y="90" rx="4" ry="4" width="32" height="25" fill="#7f7f7f" fill-opacity="1" stroke-width="0">
                      <title>Utilities: $${salary ? Math.round(Number(salary) * 0.7 * 0.08 / 12).toLocaleString() : '0'} (8%)</title>
                    </rect>
                    <text x="37" y="107" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Utilities: $${salary ? Math.round(Number(salary) * 0.7 * 0.08 / 12).toLocaleString() : '0'} (8%)</text>
                    <rect x="0" y="120" rx="4" ry="4" width="40" height="25" fill="#bcbd22" fill-opacity="1" stroke-width="0">
                      <title>Entertainment: $${salary ? Math.round(Number(salary) * 0.7 * 0.10 / 12).toLocaleString() : '0'} (10%)</title>
                    </rect>
                    <text x="45" y="137" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Entertainment: $${salary ? Math.round(Number(salary) * 0.7 * 0.10 / 12).toLocaleString() : '0'} (10%)</text>
                    <rect x="0" y="150" rx="4" ry="4" width="88" height="25" fill="#17becf" fill-opacity="1" stroke-width="0">
                      <title>Savings: $${salary ? Math.round(Number(salary) * 0.7 * 0.22 / 12).toLocaleString() : '0'} (22%)</title>
                    </rect>
                    <text x="93" y="167" fill="var(--text-primary, #1a1a1a)" font-size="11" font-weight="500">Savings: $${salary ? Math.round(Number(salary) * 0.7 * 0.22 / 12).toLocaleString() : '0'} (22%)</text>
                  </svg>
                </div>
                <div class="income-summary-row">
                  <div class="summary-stat">
                    <span class="stat-label">Monthly Net</span>
                    <span class="stat-value">$${salary ? Math.round(Number(salary) * 0.7 / 12).toLocaleString() : '0'}</span>
                  </div>
                  <div class="summary-stat">
                    <span class="stat-label">Tax Rate</span>
                    <span class="stat-value">30%</span>
                  </div>
                  <div class="summary-stat">
                    <span class="stat-label">Savings Rate</span>
                    <span class="stat-value">22%</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="fullscreen-footer">
              <div class="action-buttons-large">
                <button class="apply-btn-fullscreen">Apply for this Position</button>
                <button class="save-job-btn-fullscreen" id="save-job-${id || ''}">Save Job</button>
                <button class="share-job-btn-fullscreen">Share Job</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(fullscreenDiv);
    
    // Add event listeners for action buttons
    const applyBtn = fullscreenDiv.querySelector('.apply-btn-fullscreen');
    const saveBtn = fullscreenDiv.querySelector('.save-job-btn-fullscreen');
    const shareBtn = fullscreenDiv.querySelector('.share-job-btn-fullscreen');
    
    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        handleLoginRequired('apply');
      });
    }
    if (saveBtn) {
      // Check if job is already saved and update button text
      const jobId = id || '';
      const isSaved = (window as any).isJobSaved && (window as any).isJobSaved(jobId);
      if (isSaved) {
        saveBtn.textContent = 'Saved';
      }
      
      saveBtn.addEventListener('click', () => {
        console.log('Save job button clicked, jobId:', jobId, 'jobData:', jobData);
        
        // Call the global save function which will handle login check
        if ((window as any).toggleSaveJob) {
          (window as any).toggleSaveJob(jobId, jobData);
        } else {
          console.error('toggleSaveJob function not found');
        }
      });
    }
    if (shareBtn) {
      shareBtn.addEventListener('click', () => {
        handleLoginRequired('share');
      });
    }
  };
  
  // Global functions for saving jobs and companies
  useEffect(() => {
    // Save/unsave job function
    (window as any).toggleSaveJob = async (jobId: string, jobData: any) => {
      const authToken = localStorage.getItem('jobometry_auth_token');
      console.log('toggleSaveJob - authToken:', authToken);
      
      // Check if user is logged in first
      if (!authToken || authToken === 'null' || authToken === 'undefined') {
        console.log('No valid auth token, requesting login');
        handleLoginRequired('save');
        return;
      }
      
      try {
        // Check if it's a JWT token
        const tokenParts = authToken.split('.');
        if (tokenParts.length !== 3) {
          handleLoginRequired('save');
          return;
        }
        
        const tokenData = JSON.parse(atob(tokenParts[1]));
        const userId = tokenData.userId || tokenData.id || tokenData.sub;
        
        if (!userId) {
          handleLoginRequired('save');
          return;
        }
        
        const isSaved = savedJobIds.has(jobId);
        // Try to find the button with either class name
        const button = document.querySelector('.save-job-btn-fullscreen, .save-job-btn') as HTMLButtonElement;
        
        if (button) {
          button.textContent = 'Saving...';
          button.setAttribute('disabled', 'true');
        }
        
        if (isSaved) {
          await savedJobsService.removeSavedJob(userId, jobId);
          setSavedJobIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(jobId);
            return newSet;
          });
          if (button) {
            button.textContent = 'Save Job';
            button.removeAttribute('disabled');
          }
        } else {
          // Create job object from jobData
          const job: Job = {
            id: jobId,
            title: jobData.title || 'Unknown',
            company: jobData.company || 'Unknown',
            location: jobData.location || 'Unknown',
            salary: jobData.salary || 0,
            employmentType: jobData.employmentType || 'Full-time',
            remote: jobData.remote || false,
            coordinates: jobData.coordinates || [0, 0],
            industry: jobData.industry || 'Unknown',
            country: jobData.country || 'Unknown'
          };
          
          await savedJobsService.saveJob(userId, job);
          setSavedJobIds(prev => {
            const newSet = new Set(prev);
            newSet.add(jobId);
            return newSet;
          });
          if (button) {
            button.textContent = 'Saved';
            button.removeAttribute('disabled');
          }
        }
      } catch (error) {
        console.error('Error saving job:', error);
        alert('Failed to save job. Please try again.');
        const button = document.querySelector('.save-job-btn-fullscreen, .save-job-btn') as HTMLButtonElement;
        if (button) {
          button.textContent = savedJobIds.has(jobId) ? 'Saved' : 'Save Job';
          button.removeAttribute('disabled');
        }
      }
    };
    
    // Check if job is saved
    (window as any).isJobSaved = (jobId: string) => {
      return savedJobIds.has(jobId);
    };
    
    // Save/unsave company function
    (window as any).toggleSaveCompany = async (companyId: string, companyData: any) => {
      const authToken = localStorage.getItem('jobometry_auth_token');
      
      // Check if user is logged in first
      if (!authToken || authToken === 'null' || authToken === 'undefined') {
        handleLoginRequired('save');
        return;
      }
      
      try {
        // Check if it's a JWT token
        const tokenParts = authToken.split('.');
        if (tokenParts.length !== 3) {
          handleLoginRequired('save');
          return;
        }
        
        const tokenData = JSON.parse(atob(tokenParts[1]));
        const userId = tokenData.userId || tokenData.id || tokenData.sub;
        
        if (!userId) {
          handleLoginRequired('save');
          return;
        }
        
        const isSaved = savedCompanyIds.has(companyId);
        // Try to find the button with either class name
        const button = document.querySelector('.save-company-btn-fullscreen, .save-company-btn') as HTMLButtonElement;
        
        if (button) {
          button.textContent = 'Saving...';
          button.setAttribute('disabled', 'true');
        }
        
        if (isSaved) {
          await savedCompaniesService.removeSavedCompany(userId, companyId);
          setSavedCompanyIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(companyId);
            return newSet;
          });
          if (button) {
            button.textContent = 'Save Company';
            button.removeAttribute('disabled');
          }
        } else {
          await savedCompaniesService.saveCompany(userId, companyData);
          setSavedCompanyIds(prev => {
            const newSet = new Set(prev);
            newSet.add(companyId);
            return newSet;
          });
          if (button) {
            button.textContent = 'Saved';
            button.removeAttribute('disabled');
          }
        }
      } catch (error) {
        console.error('Error saving company:', error);
        alert('Failed to save company. Please try again.');
        const button = document.querySelector('.save-company-btn-fullscreen, .save-company-btn') as HTMLButtonElement;
        if (button) {
          button.textContent = savedCompanyIds.has(companyId) ? 'Saved' : 'Save Company';
          button.removeAttribute('disabled');
        }
      }
    };
    
    // Check if company is saved
    (window as any).isCompanySaved = (companyId: string) => {
      return savedCompanyIds.has(companyId);
    };
  }, [savedJobIds, savedCompanyIds]);
  
  // Initial jobs data conversion
  useEffect(() => {
    if (jobMarketData.features && jobMarketData.features.length > 0) {
      // Convert GeoJSON to Job objects
      const initialJobs = jobMarketData.features.map((feature: any) => {
        const props = feature.properties;
        const coords = feature.geometry.coordinates;
        
        return {
          id: props.id,
          title: props.title,
          company: props.company,
          location: props.city ? 
                  (props.state ? `${props.city}, ${props.state}` : props.city) : 
                  'Unknown Location',
          salary: props.salary,
          employmentType: props.employmentType,
          remote: props.remote,
          coordinates: coords as [number, number],
          industry: props.industryTag || 'Technology',
          country: props.countryCode || 'Unknown',
          costOfLiving: props.costOfLiving || 100
        };
      });
      
      setFilteredJobs(initialJobs);
      
      // Set default selected location based on the most common job location
      if (initialJobs.length > 0) {
        const locationCount = initialJobs.reduce((acc: any, job: Job) => {
          const locationKey = `${job.location}|${job.country}`;
          if (!acc[locationKey]) {
            acc[locationKey] = {
              count: 0,
              city: job.location.split(',')[0],
              country: job.country,
              costOfLiving: job.costOfLiving || 100,
              totalSalary: 0
            };
          }
          acc[locationKey].count++;
          acc[locationKey].totalSalary += job.salary;
          return acc;
        }, {});
        
        const topLocation = Object.values(locationCount).reduce((max: any, current: any) => 
          current.count > max.count ? current : max
        ) as any;
        
        setSelectedLocation({
          city: topLocation.city,
          country: topLocation.country,
          costOfLiving: topLocation.costOfLiving,
          averageSalary: Math.round(topLocation.totalSalary / topLocation.count)
        });
      }
    }
  }, [jobMarketData]);

  // Handle actions that require login
  const handleLoginRequired = (action: 'apply' | 'save' | 'share' | 'compare-salary' | string) => {
    // console.log('Login required for action:', action);
    setLoginModalAction(action);
    setIsLoginModalOpen(true);
  };

  // Update map data based on filters and active page
  const updateMapData = useCallback(() => {
    if (!map.current) return;
    
    // Get source for either jobs or companies based on active page
    const sourceName = activePage === 'jobs' ? 'job-markets' : 'companies';
    const source = map.current.getSource(sourceName);
    
    if (!source || !('setData' in source)) return;
    
    // Apply filters to the data
    if (activePage === 'jobs') {
      // Filter job market data
      const filteredFeatures = jobMarketData.features.filter((job: any) => {
        const props = job.properties;
        
        // Apply filters
        const matchesSearch = !activeFilters.search || 
          props.title.toLowerCase().includes(activeFilters.search.toLowerCase()) ||
          props.company.toLowerCase().includes(activeFilters.search.toLowerCase());
          
        const matchesCountry = activeFilters.countries.length === 0 || 
          activeFilters.countries.includes(props.countryCode || '');
          
        const matchesIndustry = activeFilters.industries.length === 0 || 
          activeFilters.industries.includes(props.industryTag || '');
          
        const matchesJobType = activeFilters.jobTypes.length === 0 || 
          activeFilters.jobTypes.some((type: string) => {
            const empType = props.employmentType.toLowerCase();
            
            if (type === 'Full-time' && empType === 'fulltime') return true;
            if (type === 'Part-time' && empType === 'parttime') return true;
            if (type === 'Contract' && empType === 'contract') return true;
            if (type === 'Freelance' && empType === 'freelance') return true;
            if (type === 'Internship' && empType === 'internship') return true;
            if (type === 'Remote' && props.remote === true) return true;
            
            return false;
          });
        
        const matchesPosition = activeFilters.positions.length === 0 || 
          activeFilters.positions.some((position: string) => {
            return props.title.toLowerCase().includes(position.toLowerCase());
          });
          
        const salary = props.salary || 0;
        const matchesSalary = salary >= activeFilters.salary[0] && 
                             salary <= activeFilters.salary[1];
                               
        return matchesSearch && matchesCountry && matchesIndustry && 
               matchesJobType && matchesPosition && matchesSalary;
      });
  
      const filteredData = {
        type: 'FeatureCollection' as const,
        features: filteredFeatures
      };
      
      // Update map data source
      source.setData(filteredData);
      
      // Update filtered jobs for JobList component
      const updatedJobs = filteredFeatures.map((feature: any) => {
        const props = feature.properties;
        const coords = feature.geometry.coordinates;
        
        return {
          id: props.id,
          title: props.title,
          company: props.company,
          location: props.city ? 
                   (props.state ? `${props.city}, ${props.state}` : props.city) : 
                   'Unknown Location',
          salary: props.salary,
          employmentType: props.employmentType,
          remote: props.remote,
          coordinates: coords as [number, number],
          industry: props.industryTag || 'Technology',
          country: props.countryCode || 'Unknown'
        };
      });
      
      setFilteredJobs(updatedJobs);
    } else {
      // For companies page, use all company data without filtering
      const companyGeoJSON: CompanyGeoJSON = {
        type: 'FeatureCollection',
        features: companyData.flatMap((company: any) => 
          company.locations.map((location: any) => ({
            type: 'Feature' as const,
            properties: {
              id: location.id,
              companyId: company.id,
              companyName: company.name,
              industry: company.industry,
              size: company.size,
              locationRating: location.rating,
              globalRating: company.globalRating,
              city: location.city,
              state: location.state,
              country: location.country,
              minSalary: location.salaryRange.min,
              maxSalary: location.salaryRange.max,
              openPositions: location.roles.reduce((sum: number, role: any) => sum + role.count, 0),
              isCompany: true
            },
            geometry: {
              type: 'Point' as const,
              coordinates: location.coordinates
            }
          }))
        )
      };
      
      source.setData(companyGeoJSON);
    }
  }, [activePage, activeFilters, jobMarketData, companyData]);

  // Filter jobs independently of map to ensure JobList always has data
  useEffect(() => {
    if (activePage !== 'jobs' || !jobMarketData || !jobMarketData.features) return;

    // Filter job market data based on active filters
    const filteredFeatures = jobMarketData.features.filter((job: any) => {
      const props = job.properties;
      
      // Apply filters
      const matchesSearch = !activeFilters.search || 
        props.title.toLowerCase().includes(activeFilters.search.toLowerCase()) ||
        props.company.toLowerCase().includes(activeFilters.search.toLowerCase());
        
      const matchesCountry = activeFilters.countries.length === 0 || 
        activeFilters.countries.includes(props.countryCode || '');
        
      const matchesIndustry = activeFilters.industries.length === 0 || 
        activeFilters.industries.includes(props.industryTag || '');
        
      const matchesJobType = activeFilters.jobTypes.length === 0 || 
        activeFilters.jobTypes.some((type: string) => {
          const empType = props.employmentType.toLowerCase();
          
          if (type === 'Full-time' && empType === 'fulltime') return true;
          if (type === 'Part-time' && empType === 'parttime') return true;
          if (type === 'Contract' && empType === 'contract') return true;
          if (type === 'Freelance' && empType === 'freelance') return true;
          if (type === 'Internship' && empType === 'internship') return true;
          if (type === 'Remote' && props.remote === true) return true;
          
          return false;
        });
        
      const matchesPosition = activeFilters.positions.length === 0 || 
        activeFilters.positions.some((position: string) => {
          return props.title.toLowerCase().includes(position.toLowerCase());
        });
        
      const salary = props.salary || 0;
      const matchesSalary = salary >= activeFilters.salary[0] && 
                           salary <= activeFilters.salary[1];
                             
      return matchesSearch && matchesCountry && matchesIndustry && 
             matchesJobType && matchesPosition && matchesSalary;
    });

    // Convert filtered features to Job objects for JobList
    const updatedJobs = filteredFeatures.map((feature: any) => {
      const props = feature.properties;
      const coords = feature.geometry.coordinates;
      
      return {
        id: props.id,
        title: props.title,
        company: props.company,
        location: props.city ? 
                 (props.state ? `${props.city}, ${props.state}` : props.city) : 
                 'Unknown Location',
        salary: props.salary,
        employmentType: props.employmentType,
        remote: props.remote,
        coordinates: coords as [number, number],
        industry: props.industryTag || 'Technology',
        country: props.countryCode || 'Unknown'
      };
    });
    
    setFilteredJobs(updatedJobs);
  }, [activePage, activeFilters, jobMarketData]);

  // Add map layers for jobs and companies
  const addMapLayers = useCallback(() => {
    if (!map.current) return;

    // Job layers
    if (!map.current.getLayer('job-clusters')) {
      map.current.addLayer({
        id: 'job-clusters',
        type: 'circle',
        source: 'job-markets',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            'rgba(81, 187, 214, 0.6)', // blue for small clusters
            100, 'rgba(241, 240, 117, 0.6)', // yellow for medium clusters
            750, 'rgba(242, 140, 177, 0.6)'  // pink for large clusters
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            15, // radius for small clusters
            100, 20, // radius for medium clusters
            750, 25  // radius for large clusters
          ],
          'circle-blur': 0.4,
          'circle-opacity': 0.8
        },
        layout: {
          'visibility': activePage === 'jobs' ? 'visible' : 'none'
        }
      });
    }

    if (!map.current.getLayer('job-cluster-count')) {
      map.current.addLayer({
        id: 'job-cluster-count',
        type: 'symbol',
        source: 'job-markets',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'visibility': activePage === 'jobs' ? 'visible' : 'none'
        }
      });
    }

    if (!map.current.getLayer('unclustered-jobs')) {
      map.current.addLayer({
        id: 'unclustered-jobs',
        type: 'circle',
        source: 'job-markets',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#11b4da',
          'circle-radius': 4,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff'
        },
        layout: {
          'visibility': activePage === 'jobs' ? 'visible' : 'none'
        }
      });
    }
    
    // Company layers (with different colors)
    if (!map.current.getLayer('company-clusters')) {
      map.current.addLayer({
        id: 'company-clusters',
        type: 'circle',
        source: 'companies',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            'rgba(76, 175, 80, 0.6)', // green for small clusters
            100, 'rgba(255, 193, 7, 0.6)', // amber for medium clusters
            750, 'rgba(255, 87, 34, 0.6)'  // deep orange for large clusters
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            15, // radius for small clusters
            100, 20, // radius for medium clusters
            750, 25  // radius for large clusters
          ],
          'circle-blur': 0.4,
          'circle-opacity': 0.8
        },
        layout: {
          'visibility': activePage === 'companies' ? 'visible' : 'none'
        }
      });
    }

    if (!map.current.getLayer('company-cluster-count')) {
      map.current.addLayer({
        id: 'company-cluster-count',
        type: 'symbol',
        source: 'companies',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'visibility': activePage === 'companies' ? 'visible' : 'none'
        }
      });
    }

    if (!map.current.getLayer('unclustered-companies')) {
      map.current.addLayer({
        id: 'unclustered-companies',
        type: 'circle',
        source: 'companies',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#4CAF50',
          'circle-radius': 5,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff'
        },
        layout: {
          'visibility': activePage === 'companies' ? 'visible' : 'none'
        }
      });
    }

    // Add click events for jobs
    map.current.on('click', 'job-clusters', (e) => {
      const features = map.current?.queryRenderedFeatures(e.point, { layers: ['job-clusters'] });
      if (!features?.length || !map.current) return;
      
      const clusterId = features[0].properties?.cluster_id;
      if (!clusterId) return;
      
      const source = map.current.getSource('job-markets');
      if (source && 'getClusterExpansionZoom' in source) {
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || !map.current || !features?.[0].geometry) return;
          
          const coordinates = (features[0].geometry as Point).coordinates;
          map.current.easeTo({
            center: [coordinates[0], coordinates[1]],
            zoom: zoom || map.current.getZoom() + 1
          });
        });
      }
    });

    // Add hover effect for jobs
    let jobHoverPopup: mapboxgl.Popup | null = null;
    
    // Change cursor on hover
    map.current.on('mouseenter', 'unclustered-jobs', () => {
      if (!map.current) return;
      map.current.getCanvas().style.cursor = 'pointer';
    });
    
    map.current.on('mouseleave', 'unclustered-jobs', () => {
      if (!map.current) return;
      map.current.getCanvas().style.cursor = '';
    });
    
    map.current.on('mouseenter', 'unclustered-jobs', (e) => {
      if (!e.features?.[0].geometry || !map.current) return;
      
      const coordinates = (e.features[0].geometry as Point).coordinates.slice();
      const props = e.features[0].properties || {};
      
      // Create hover popup
      jobHoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -15],
        className: 'job-hover-popup-container'
      })
        .setLngLat(coordinates as [number, number])
        .setHTML(`
          <div class="job-hover-popup ${darkMode ? 'dark' : ''}">
            <h3>${props.title || 'Job Position'}</h3>
            <div class="company-info">
              <span class="company-name">${props.company || 'Company'}</span>
              ${props.companyRating ? `<span class="rating">★ ${Number(props.companyRating).toFixed(1)}</span>` : ''}
            </div>
            <div class="location">${props.city}${props.state ? `, ${props.state}` : ''}</div>
            <div class="salary">${props.salary ? `$${Number(props.salary).toLocaleString()}` : 'Salary not disclosed'}</div>
            <div class="employment-info">
              ${props.employmentType || 'Full-time'} ${props.remote ? '• Remote' : ''}
            </div>
            <div class="hover-hint">Click for full details & comparisons</div>
          </div>
        `)
        .addTo(map.current);
    });
    
    map.current.on('mouseleave', 'unclustered-jobs', () => {
      if (jobHoverPopup) {
        jobHoverPopup.remove();
        jobHoverPopup = null;
      }
    });
    
    // Add hover effect for companies
    let companyHoverPopup: mapboxgl.Popup | null = null;
    
    // Change cursor on hover for companies
    map.current.on('mouseenter', 'unclustered-companies', () => {
      if (!map.current) return;
      map.current.getCanvas().style.cursor = 'pointer';
      // console.log('Mouse entered company layer');
    });
    
    map.current.on('mouseleave', 'unclustered-companies', () => {
      if (!map.current) return;
      map.current.getCanvas().style.cursor = '';
      // console.log('Mouse left company layer');
    });
    
    map.current.on('mouseenter', 'unclustered-companies', (e) => {
      if (!e.features?.[0].geometry || !map.current) return;
      
      const coordinates = (e.features[0].geometry as Point).coordinates.slice();
      const props = e.features[0].properties || {};
      
      // Debug logging with more details
      // console.log('Company hover event triggered');
      // console.log('Company name:', props.companyName);
      // console.log('All properties:', JSON.stringify(props, null, 2));
      // console.log('Coordinates:', coordinates);
      
      // Validate required properties
      if (!props.companyName) {
        // console.warn('Company name is missing for feature:', e.features[0]);
      }
      
      // Remove any existing company hover popup
      if (companyHoverPopup) {
        companyHoverPopup.remove();
        companyHoverPopup = null;
      }
      
      try {
        // Create hover popup
        companyHoverPopup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: [0, -25],
          className: 'company-hover-popup-container'
        })
          .setLngLat(coordinates as [number, number])
          .setHTML(`
          <div class="company-hover-popup ${darkMode ? 'dark' : ''}">
            <div class="company-header">
              <h3>${props.companyName || 'Company'}</h3>
              <div class="company-meta">
                <span class="industry">${props.industry || 'Technology'}</span>
                <span class="size">${props.size || 'Large'} Company</span>
              </div>
            </div>
            
            <div class="glassdoor-ratings">
              <div class="overall-rating">
                <div class="rating-circle">
                  <span class="rating-number">${props.locationRating ? Number(props.locationRating).toFixed(1) : 'N/A'}</span>
                </div>
                <div class="rating-info">
                  <div class="rating-stars">
                    ${props.locationRating ? '★'.repeat(Math.floor(Number(props.locationRating))) + '☆'.repeat(5 - Math.floor(Number(props.locationRating))) : '☆☆☆☆☆'}
                  </div>
                  <div class="rating-label">Overall Rating</div>
                </div>
              </div>
            </div>
            
            <div class="quick-stats">
              <div class="stat">
                <span class="stat-value">${props.minSalary && props.maxSalary ? `$${(Number(props.minSalary) / 1000).toFixed(0)}k-${(Number(props.maxSalary) / 1000).toFixed(0)}k` : 'Competitive'}</span>
                <span class="stat-label">Salary Range</span>
              </div>
              <div class="stat">
                <span class="stat-value">${props.openPositions || 0}</span>
                <span class="stat-label">Open Jobs</span>
              </div>
              <div class="stat">
                <span class="stat-value">${props.city || 'Multiple'}</span>
                <span class="stat-label">Location</span>
              </div>
            </div>
            
            <div class="hover-hint">
              Click for full company details & ratings
            </div>
          </div>
        `)
        .addTo(map.current);
      } catch (error) {
        console.error('Error creating company hover popup:', error);
      }
    });
    
    map.current.on('mouseleave', 'unclustered-companies', () => {
      if (companyHoverPopup) {
        companyHoverPopup.remove();
        companyHoverPopup = null;
      }
    });
    
    map.current.on('click', 'unclustered-jobs', (e) => {
      if (!e.features?.[0].geometry || !map.current) return;
      
      // Remove hover popup
      if (jobHoverPopup) {
        jobHoverPopup.remove();
        jobHoverPopup = null;
      }
      
      const coordinates = (e.features[0].geometry as Point).coordinates.slice();
      const properties = e.features[0].properties || {};
      
      // Format location
      const location = properties.state ? `${properties.city}, ${properties.state}` : `${properties.city}`;
      
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      
      // Remove any existing popups
      document.querySelectorAll('.mapboxgl-popup').forEach(popup => {
        popup.remove();
      });
      
      // Show job details popup with all properties
      showJobDetailsPopup({
        ...properties,
        location,
        coordinates: coordinates as [number, number]
      });
    });
    
    // This duplicate handler was removed - using the first one above with full-screen popup
    
    // Add click events for companies
    map.current.on('click', 'company-clusters', (e) => {
      const features = map.current?.queryRenderedFeatures(e.point, { layers: ['company-clusters'] });
      if (!features?.length || !map.current) return;
      
      const clusterId = features[0].properties?.cluster_id;
      if (!clusterId) return;
      
      const source = map.current.getSource('companies');
      if (source && 'getClusterExpansionZoom' in source) {
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || !map.current || !features?.[0].geometry) return;
          
          const coordinates = (features[0].geometry as Point).coordinates;
          map.current.easeTo({
            center: [coordinates[0], coordinates[1]],
            zoom: zoom || map.current.getZoom() + 1
          });
        });
      }
    });

    map.current.on('click', 'unclustered-companies', (e) => {
      if (!e.features?.[0].geometry || !map.current) return;
      
      // Remove any existing popups
      document.querySelectorAll('.mapboxgl-popup').forEach(popup => {
        popup.remove();
      });
      
      const props = e.features[0].properties || {};
      const companyId = props.companyId || props.id;
      
      // Find the company data
      const company = companyData.find((c: any) => c.id === companyId);
      if (company) {
        setFullscreenCompany(company);
      }
    });
    // Add hover popups for job clusters
    let hoverPopup: mapboxgl.Popup | null = null;
    
    map.current?.on('mouseenter', 'job-clusters', (e) => {
      if (!map.current || !e.features?.[0]) return;
      
      map.current.getCanvas().style.cursor = 'pointer';
      
      const coordinates = (e.features[0].geometry as any).coordinates.slice();
      const pointCount = e.features[0].properties?.point_count;
      
      // Get cluster leaves to calculate average salary
      const clusterId = e.features[0].properties?.cluster_id;
      const source = map.current.getSource('job-markets') as any;
      
      if (source && clusterId !== undefined) {
        source.getClusterLeaves(clusterId, 100, 0, (err: any, features: any[]) => {
          if (err || !features) return;
          
          const totalSalary = features.reduce((sum, f) => sum + (f.properties.salary || 0), 0);
          const avgSalary = Math.round(totalSalary / features.length);
          
          // Get common companies
          const companyCount: { [key: string]: number } = {};
          features.forEach(f => {
            const company = f.properties.company;
            if (company) {
              companyCount[company] = (companyCount[company] || 0) + 1;
            }
          });
          
          const topCompanies = Object.entries(companyCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([company]) => company);
          
          // Create hover popup
          hoverPopup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat(coordinates)
            .setHTML(`
              <div style="padding: 12px; min-width: 200px;">
                <div style="font-weight: 600; margin-bottom: 8px; font-size: 16px;">Job Cluster</div>
                <div style="margin-bottom: 6px;">
                  <span style="color: #3b82f6; font-size: 20px; font-weight: bold;">${pointCount}</span>
                  <span style="color: #6b7280; font-size: 14px;"> positions</span>
                </div>
                <div style="font-size: 13px; color: #059669; margin-bottom: 8px;">
                  Average salary: <strong>$${(avgSalary / 1000).toFixed(0)}k</strong>
                </div>
                ${topCompanies.length > 0 ? `
                  <div style="font-size: 12px; color: #6b7280; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                    <div style="margin-bottom: 4px;">Top companies:</div>
                    ${topCompanies.map(c => `<div>• ${c}</div>`).join('')}
                  </div>
                ` : ''}
                <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                  Click to zoom in
                </div>
              </div>
            `)
            .addTo(map.current!);
        });
      }
    });
    
    map.current?.on('mouseleave', 'job-clusters', () => {
      if (map.current) map.current.getCanvas().style.cursor = '';
      if (hoverPopup) {
        hoverPopup.remove();
        hoverPopup = null;
      }
    });
    
    // Add hover for individual jobs
    map.current?.on('mouseenter', 'unclustered-jobs', (e) => {
      if (!map.current || !e.features?.[0]) return;
      
      map.current.getCanvas().style.cursor = 'pointer';
      
      const coordinates = (e.features[0].geometry as any).coordinates.slice();
      const props = e.features[0].properties || {};
      
      hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 10
      })
        .setLngLat(coordinates)
        .setHTML(`
          <div style="padding: 10px; min-width: 180px;">
            <div style="font-weight: 600; margin-bottom: 4px;">${props.title || 'Position'}</div>
            <div style="font-size: 14px; color: #6b7280; margin-bottom: 4px;">${props.company || 'Company'}</div>
            <div style="font-size: 13px; color: #059669;">
              ${props.salary ? `$${(props.salary / 1000).toFixed(0)}k` : 'Salary not disclosed'}
            </div>
            <div style="font-size: 11px; color: #9ca3af; margin-top: 6px;">
              Click for details
            </div>
          </div>
        `)
        .addTo(map.current);
    });
    
    map.current?.on('mouseleave', 'unclustered-jobs', () => {
      if (map.current) map.current.getCanvas().style.cursor = '';
      if (hoverPopup) {
        hoverPopup.remove();
        hoverPopup = null;
      }
    });
    
    // Add hover popups for company clusters
    map.current?.on('mouseenter', 'company-clusters', (e) => {
      if (!map.current || !e.features?.[0]) return;
      
      map.current.getCanvas().style.cursor = 'pointer';
      
      const coordinates = (e.features[0].geometry as any).coordinates.slice();
      const pointCount = e.features[0].properties?.point_count;
      
      // Get cluster leaves to calculate statistics
      const clusterId = e.features[0].properties?.cluster_id;
      const source = map.current.getSource('companies') as any;
      
      if (source && clusterId !== undefined) {
        source.getClusterLeaves(clusterId, 100, 0, (err: any, features: any[]) => {
          if (err || !features) return;
          
          // Calculate average salary and total positions
          let totalMinSalary = 0;
          let totalMaxSalary = 0;
          let totalPositions = 0;
          const industries = new Set<string>();
          const uniqueCompanies = new Set<string>();
          
          features.forEach(f => {
            totalMinSalary += f.properties.minSalary || 0;
            totalMaxSalary += f.properties.maxSalary || 0;
            totalPositions += f.properties.openPositions || 0;
            if (f.properties.industry) industries.add(f.properties.industry);
            if (f.properties.companyName) uniqueCompanies.add(f.properties.companyName);
          });
          
          const avgMinSalary = Math.round(totalMinSalary / features.length);
          const avgMaxSalary = Math.round(totalMaxSalary / features.length);
          
          // Get top industries
          const industryArray = Array.from(industries).slice(0, 3);
          
          // Create hover popup
          hoverPopup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat(coordinates)
            .setHTML(`
              <div style="padding: 12px; min-width: 220px;">
                <div style="font-weight: 600; margin-bottom: 8px; font-size: 16px;">Company Cluster</div>
                <div style="margin-bottom: 6px;">
                  <span style="color: #8b5cf6; font-size: 20px; font-weight: bold;">${uniqueCompanies.size}</span>
                  <span style="color: #6b7280; font-size: 14px;"> companies</span>
                </div>
                <div style="margin-bottom: 6px;">
                  <span style="color: #3b82f6; font-size: 16px; font-weight: bold;">${totalPositions}</span>
                  <span style="color: #6b7280; font-size: 14px;"> open positions</span>
                </div>
                <div style="font-size: 13px; color: #059669; margin-bottom: 8px;">
                  Salary range: <strong>$${(avgMinSalary / 1000).toFixed(0)}k - $${(avgMaxSalary / 1000).toFixed(0)}k</strong>
                </div>
                ${industryArray.length > 0 ? `
                  <div style="font-size: 12px; color: #6b7280; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                    <div style="margin-bottom: 4px;">Industries:</div>
                    ${industryArray.map(i => `<div>• ${i}</div>`).join('')}
                  </div>
                ` : ''}
                <div style="font-size: 11px; color: #9ca3af; margin-top: 8px;">
                  Click to zoom in
                </div>
              </div>
            `)
            .addTo(map.current!);
        });
      }
    });
    
    map.current?.on('mouseleave', 'company-clusters', () => {
      if (map.current) map.current.getCanvas().style.cursor = '';
      if (hoverPopup) {
        hoverPopup.remove();
        hoverPopup = null;
      }
    });
    
  }, [activePage, darkMode]);

  // Initialize map once - separate effect for initialization trigger
  useEffect(() => {
    if (activePage === 'salaries' || activePage === 'landing') {
      // Clean up map when navigating to salaries or landing
      if (map.current) {
        map.current.remove();
        map.current = null;
        setMapInstance(null);
      }
      return;
    }
    
    // Initialize map if it doesn't exist and we're on jobs/companies page
    if (!map.current && mapContainer.current && (activePage === 'jobs' || activePage === 'companies')) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: darkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
        center: [-96, 37.8], // Center on USA
        zoom: 3,
        attributionControl: false
      });

      map.current.on('load', () => {
        // Set the map instance state
        setMapInstance(map.current);
        
        // Add job data source
        if (map.current && !map.current.getSource('job-markets')) {
          console.log('Adding job-markets source with', jobMarketData.features.length, 'features');
          map.current.addSource('job-markets', {
            type: 'geojson',
            data: jobMarketData,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50
          });
        }
        
        // Add company data source
        if (map.current && !map.current.getSource('companies')) {
          console.log('Adding companies source, total companies:', companyData.length);
          const companyFeatures = companyData.flatMap((company: any) => 
            company.locations.map((location: any) => ({
                type: 'Feature' as const,
                properties: {
                  id: location.id,
                  companyId: company.id,
                  companyName: company.name,
                  industry: company.industry,
                  size: company.size,
                  locationRating: location.rating,
                  globalRating: company.globalRating,
                  city: location.city,
                  state: location.state,
                  country: location.country,
                  minSalary: location.salaryRange.min,
                  maxSalary: location.salaryRange.max,
                  openPositions: location.roles.reduce((sum: number, role: any) => sum + role.count, 0),
                  isCompany: true
                },
                geometry: {
                  type: 'Point' as const,
                  coordinates: location.coordinates
                }
              }))
            );
          
          console.log('Total company locations (features):', companyFeatures.length);
          console.log('Sample company feature:', companyFeatures[0]);
          
          const companyGeoJSON: CompanyGeoJSON = {
            type: 'FeatureCollection',
            features: companyFeatures
          };
          
          map.current.addSource('companies', {
            type: 'geojson',
            data: companyGeoJSON,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50
          });
        }
        
        // Add map layers
        addMapLayers();
      });
    }

    // Don't cleanup in the return function - we'll handle it when switching pages
  }, [activePage, jobMarketData, companyData]);

  // Handle style change (dark/light mode)
  useEffect(() => {
    if (!map.current) return;
    
    map.current.setStyle(darkMode 
      ? 'mapbox://styles/mapbox/dark-v11'
      : 'mapbox://styles/mapbox/light-v11'
    );

    map.current.once('style.load', () => {
      // Re-add sources and layers after style change
      if (map.current && !map.current.getSource('job-markets')) {
        map.current.addSource('job-markets', {
          type: 'geojson',
          data: jobMarketData,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        });
      }
      
      if (map.current && !map.current.getSource('companies')) {
        const companyGeoJSON: CompanyGeoJSON = {
          type: 'FeatureCollection',
          features: companyData.flatMap((company: any) => 
            company.locations.map((location: any) => ({
              type: 'Feature' as const,
              properties: {
                id: location.id,
                companyId: company.id,
                companyName: company.name,
                industry: company.industry,
                size: company.size,
                locationRating: location.rating,
                globalRating: company.globalRating,
                city: location.city,
                state: location.state,
                country: location.country,
                minSalary: location.salaryRange.min,
                maxSalary: location.salaryRange.max,
                openPositions: location.roles.reduce((sum: number, role: any) => sum + role.count, 0),
                isCompany: true
              },
              geometry: {
                type: 'Point' as const,
                coordinates: location.coordinates
              }
            }))
          )
        };
        
        map.current.addSource('companies', {
          type: 'geojson',
          data: companyGeoJSON,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50
        });
      }
      
      addMapLayers();
    });
  }, [darkMode]);

  // Update data visualization when filters change
  useEffect(() => {
    if (!map.current) return;
    
    // Apply filters to displayed data
    updateMapData();
  }, [activeFilters, updateMapData]);

  // Filter jobs even when map is not ready
  useEffect(() => {
    if (activePage !== 'jobs' || !jobMarketData) return;

    const features = jobMarketData.features || [];
    const filteredFeatures = features.filter((feature: any) => {
      const props = feature.properties || {};
      
      const matchesSearch = !activeFilters.search || 
        props.title?.toLowerCase().includes(activeFilters.search.toLowerCase()) ||
        props.company?.toLowerCase().includes(activeFilters.search.toLowerCase());
        
      const matchesCountry = activeFilters.countries.length === 0 || 
        activeFilters.countries.includes(props.countryCode || 'Unknown');
        
      const matchesIndustry = activeFilters.industries.length === 0 || 
        activeFilters.industries.includes(props.industryTag || 'Technology');
        
      const matchesJobType = activeFilters.jobTypes.length === 0 || 
        activeFilters.jobTypes.some((type: string) => {
          if (type === 'Remote' && props.remote) return true;
          if (type === props.employmentType) return true;
          return false;
        });
      
      const matchesPosition = activeFilters.positions.length === 0 || 
        activeFilters.positions.some((position: string) => {
          return props.title?.toLowerCase().includes(position.toLowerCase());
        });
        
      const salary = props.salary || 0;
      const matchesSalary = salary >= activeFilters.salary[0] && 
                           salary <= activeFilters.salary[1];
                             
      return matchesSearch && matchesCountry && matchesIndustry && 
             matchesJobType && matchesPosition && matchesSalary;
    });

    const updatedJobs = filteredFeatures.map((feature: any) => {
      const props = feature.properties;
      const coords = feature.geometry.coordinates;
      
      return {
        id: props.id,
        title: props.title,
        company: props.company,
        location: props.city ? 
                 (props.state ? `${props.city}, ${props.state}` : props.city) : 
                 'Unknown Location',
        salary: props.salary,
        employmentType: props.employmentType,
        remote: props.remote,
        coordinates: coords as [number, number],
        industry: props.industryTag || 'Technology',
        country: props.countryCode || 'Unknown',
        costOfLiving: props.costOfLiving || 100
      };
    });
    
    setFilteredJobs(updatedJobs);
  }, [activePage, activeFilters, jobMarketData]);

  // Toggle visibility of layers based on active page
  useEffect(() => {
    if (!map.current) return;
    
    const jobLayerIds = ['job-clusters', 'job-cluster-count', 'unclustered-jobs'];
    const companyLayerIds = ['company-clusters', 'company-cluster-count', 'unclustered-companies'];
    
    if (activePage === 'jobs') {
      // Show job layers, hide company layers
      jobLayerIds.forEach(id => {
        if (map.current?.getLayer(id)) {
          map.current.setLayoutProperty(id, 'visibility', 'visible');
        }
      });
      
      companyLayerIds.forEach(id => {
        if (map.current?.getLayer(id)) {
          map.current.setLayoutProperty(id, 'visibility', 'none');
        }
      });
    } else if (activePage === 'companies') {
      // Show company layers, hide job layers
      jobLayerIds.forEach(id => {
        if (map.current?.getLayer(id)) {
          map.current.setLayoutProperty(id, 'visibility', 'none');
        }
      });
      
      companyLayerIds.forEach(id => {
        if (map.current?.getLayer(id)) {
          map.current.setLayoutProperty(id, 'visibility', 'visible');
        }
      });
    }
  }, [activePage]);

  // Toggle dark/light mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    // Update map style when mode changes
    if (map.current) {
      map.current.setStyle(newDarkMode 
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11'
      );
      
      // Re-add sources and layers after style change completes
      map.current.once('style.load', () => {
        // Add data sources
        if (map.current && !map.current.getSource('job-markets')) {
          map.current.addSource('job-markets', {
            type: 'geojson',
            data: jobMarketData,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50
          });
        }
        
        if (map.current && !map.current.getSource('companies')) {
          const companyGeoJSON: CompanyGeoJSON = {
            type: 'FeatureCollection',
            features: companyData.flatMap((company: any) => 
              company.locations.map((location: any) => ({
                type: 'Feature' as const,
                properties: {
                  id: location.id,
                  companyId: company.id,
                  companyName: company.name,
                  industry: company.industry,
                  size: company.size,
                  locationRating: location.rating,
                  globalRating: company.globalRating,
                  city: location.city,
                  state: location.state,
                  country: location.country,
                  minSalary: location.salaryRange.min,
                  maxSalary: location.salaryRange.max,
                  openPositions: location.roles.reduce((sum: number, role: any) => sum + role.count, 0),
                  isCompany: true
                },
                geometry: {
                  type: 'Point' as const,
                  coordinates: location.coordinates
                }
              }))
            )
          };
          
          map.current.addSource('companies', {
            type: 'geojson',
            data: companyGeoJSON,
            cluster: true,
            clusterMaxZoom: 14,
            clusterRadius: 50
          });
        }
        
        addMapLayers();
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: JobFiltersType) => {
    setActiveFilters({ ...activeFilters, ...newFilters });
    // Close mobile filter sheet after applying
    setIsMobileFilterOpen(false);
  };
  
  const getActiveFiltersCount = () => {
    let count = 0;
    if (activeFilters.search) count++;
    if (activeFilters.countries.length > 0) count++;
    if (activeFilters.industries.length > 0) count++;
    if (activeFilters.jobTypes.length > 0) count++;
    if (activeFilters.positions.length > 0) count++;
    if (activeFilters.salary[0] > 0 || activeFilters.salary[1] < 200000) count++;
    return count;
  };

  // Handle search from landing page
  const handleLandingPageSearch = (filters: {
    position: string;
    salary: [number, number];
    location: string;
    remote: boolean;
    experience: string;
  }) => {
    // Convert landing page filters to job filters
    const newFilters: JobFiltersType = {
      search: filters.position,
      countries: [],
      industries: [],
      jobTypes: filters.remote ? ['Remote'] : [],
      positions: filters.position ? [filters.position] : [],
      salary: filters.salary
    };
    
    setActiveFilters(newFilters);
    setActivePage('jobs');
    setIsMobileSidebarOpen(false);
  };

  // Handle navigation tab changes
  const handleNavigation = (page: string) => {
    // Handle profile navigation with specific tabs
    if (page === 'resume' || page === 'settings' || page === 'referrals') {
      setActivePage('profile');
      setProfileTab(
        page === 'resume' ? 'resume' :
        page === 'settings' ? 'preferences' :
        page === 'referrals' ? 'referrals' :
        'profile'
      );
    } else if (page === 'profile') {
      setActivePage('profile');
      setProfileTab('profile'); // Reset to profile tab when navigating to profile directly
    } else {
      setActivePage(page);
      // Reset profile tab when navigating away from profile
      if (activePage === 'profile') {
        setProfileTab('profile');
      }
    }
    
    setIsMobileSidebarOpen(false); // Close mobile sidebar when switching pages
    
    // Clear company filters when navigating away from companies page
    if (page !== 'companies') {
      setCompanyFilters({});
    }
  };

  // Handle navigation to companies page with filters
  const handleNavigateToCompanies = (filters: { company?: string; location?: string }) => {
    console.log('Navigating to companies with filters:', filters);
    
    // Set company filters
    setCompanyFilters({
      searchTerm: filters.company || '',
      countries: filters.location ? [filters.location] : []
    });
    
    setActivePage('companies');
    setIsMobileSidebarOpen(false);
  };

  // Handle navigation to jobs page with filters
  const handleNavigateToJobs = (filters: { position?: string; location?: string; salaryMin?: number }) => {
    console.log('Navigating to jobs with filters:', filters);
    
    // Convert navigation filters to job filters
    const newFilters: JobFiltersType = {
      search: '', // Don't set search to avoid duplicates with positions
      countries: filters.location ? [filters.location] : [], // Add country filter
      industries: [],
      jobTypes: [],
      positions: filters.position ? [filters.position] : [],
      salary: filters.salaryMin ? [filters.salaryMin, 200000] : [0, 200000] // Only set minimum salary
    };
    
    setActiveFilters(newFilters);
    setActivePage('jobs');
    setIsMobileSidebarOpen(false);
  };

  // Handle mobile sidebar toggle
  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };
  
  // Load saved jobs and companies on mount
  useEffect(() => {
    const loadSavedItems = async () => {
      const authToken = localStorage.getItem('jobometry_auth_token');
      if (!authToken) return;
      
      // Parse token to get user ID (mock implementation)
      try {
        // Check if it's a JWT token
        const tokenParts = authToken.split('.');
        if (tokenParts.length !== 3) {
          alert('Invalid authentication. Please log in again.');
          return;
        }
        
        const tokenData = JSON.parse(atob(tokenParts[1]));
        const userId = tokenData.userId || tokenData.id || tokenData.sub;
        
        if (userId) {
          // Load saved jobs
          const savedJobs = await savedJobsService.getSavedJobs(userId);
          setSavedJobIds(new Set(savedJobs.map(job => job.id)));
          
          // Load saved companies (if we have a service for it)
          // const savedCompanies = await savedCompaniesService.getSavedCompanies(userId);
          // setSavedCompanyIds(new Set(savedCompanies.map(company => company.id)));
        }
      } catch (error) {
        // console.error('Error loading saved items:', error);
      }
    };
    
    loadSavedItems();
  }, []);

  return (
    <AuthProvider>
      <ComparisonProvider>
        <div className={`app ${darkMode ? 'dark' : 'light'}`} data-page={activePage}>
        <Header 
          darkMode={darkMode} 
          activePage={activePage}
          onNavigate={handleNavigation}
          onMobileMenuToggle={() => setIsMobileNavOpen(true)}
          showMobileMenu={isMobileSidebarOpen}
          filters={activeFilters}
          filteredJobsCount={filteredJobs.length}
          totalJobsCount={jobMarketData.features ? jobMarketData.features.length : 0}
          companiesCount={companyData.length}
          onLoginClick={() => {
            setLoginModalMode('login');
            setIsLoginModalOpen(true);
          }}
          onSignupClick={() => {
            setLoginModalMode('signup');
            setIsLoginModalOpen(true);
          }}
        />
        
        <MobileNav 
          isOpen={isMobileNavOpen}
          onClose={() => setIsMobileNavOpen(false)}
          activePage={activePage}
          onNavigate={handleNavigation}
          darkMode={darkMode}
        />
        
        <MapProvider map={mapInstance}>
        {/* Mobile overlay */}
        {isMobileSidebarOpen && (
          <div 
            className="mobile-overlay"
            onClick={handleMobileSidebarToggle}
          />
        )}
        
        <div className="main-content">
          {/* Sidebar with different content based on active page */}
          {activePage === 'jobs' && (
            <Sidebar 
              onFilterChange={handleFilterChange} 
              filters={activeFilters} 
              darkMode={darkMode}
              jobs={filteredJobs}
              isOpen={isMobileSidebarOpen}
              onToggle={handleMobileSidebarToggle}
              selectedLocation={selectedLocation}
              onLoginRequired={handleLoginRequired}
              onJobClick={handleJobClick}
            />
          )}
          
          {activePage === 'companies' && (
            <div className="sidebar-fixed">
              <Companies 
                companies={companyData} 
                darkMode={darkMode}
                onCompanySelect={setSelectedCompany}
                onViewReferralDashboard={(companyId, companyName) => setReferralDashboard({ companyId, companyName })}
                initialFilters={companyFilters}
              />
            </div>
          )}

          {activePage === 'salaries' && (
            <Salaries 
              darkMode={darkMode}
              onLoginRequired={handleLoginRequired}
              onNavigateToCompanies={handleNavigateToCompanies}
              onNavigateToJobs={handleNavigateToJobs}
            />
          )}

          {activePage === 'landing' && (
            <LandingPage 
              darkMode={darkMode}
              onSearch={handleLandingPageSearch}
              onLoginRequired={handleLoginRequired}
            />
          )}

          {activePage === 'profile' && (
            <div className="profile-page-container" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: darkMode ? '#0a0a0a' : '#f5f5f5',
              zIndex: 100,
              overflow: 'auto',
              padding: '20px'
            }}>
              <ProfileSettings 
                darkMode={darkMode}
                initialTab={profileTab}
                onClose={() => setActivePage('jobs')}
                onNavigate={(page, data) => {
                  handleNavigation(page);
                  // Handle specific navigation data
                  if (data?.focusCompanyId && page === 'companies') {
                    setTimeout(() => {
                      const company = companyData.find((c: any) => c.id === data.focusCompanyId);
                      if (company) setSelectedCompany(company);
                    }, 100);
                  }
                }}
              />
            </div>
          )}
          
          {/* Map container is always shown (except for salaries, landing, and profile pages) */}
          {activePage !== 'salaries' && activePage !== 'landing' && activePage !== 'profile' && (
            <>
              <div ref={mapContainer} className="map-container" />
              {/* Mobile Map Controls */}
              {activePage === 'jobs' && (
                <MobileMapControls
                  onZoomIn={() => map.current?.zoomIn()}
                  onZoomOut={() => map.current?.zoomOut()}
                  onRecenter={() => map.current?.flyTo({ center: [-96, 37.8], zoom: 3 })}
                  onToggleFilters={() => setIsMobileFilterOpen(true)}
                  activeFiltersCount={getActiveFiltersCount()}
                  mapStyle={darkMode ? 'dark' : 'streets'}
                  onStyleChange={(style) => {
                    if (map.current) {
                      const styleUrl = style === 'dark' 
                        ? 'mapbox://styles/mapbox/dark-v10'
                        : style === 'satellite' 
                        ? 'mapbox://styles/mapbox/satellite-v9'
                        : 'mapbox://styles/mapbox/streets-v11';
                      map.current.setStyle(styleUrl);
                    }
                  }}
                />
              )}
            </>
          )}
          
          {/* Mobile Filter Sheet for Jobs */}
          {activePage === 'jobs' && (
            <MobileFilterSheet
              isOpen={isMobileFilterOpen}
              onClose={() => setIsMobileFilterOpen(false)}
              filters={activeFilters}
              onFilterChange={handleFilterChange}
              availableOptions={{
                countries: ['United States', 'Canada', 'United Kingdom', 'Germany', 'Netherlands', 'Australia'].map(c => ({ value: c, label: c })),
                industries: ['Technology', 'Finance', 'Healthcare', 'Education', 'Retail', 'Manufacturing'].map(i => ({ value: i, label: i })),
                jobTypes: ['Full-time', 'Part-time', 'Contract', 'Remote', 'Hybrid'].map(t => ({ value: t, label: t })),
                positions: ['Software Engineer', 'Data Scientist', 'Product Manager', 'Designer', 'Sales', 'Marketing'].map(p => ({ value: p, label: p }))
              }}
            />
          )}
          
          {/* Company Details Panel */}
          {selectedCompany && (
            <CompanyDetailsPanel
              company={selectedCompany}
              onClose={() => setSelectedCompany(null)}
              darkMode={darkMode}
              onLoginRequired={handleLoginRequired}
            />
          )}
          
          {/* Fullscreen Company Details */}
          {fullscreenCompany && (
            <div className="company-fullscreen-overlay">
              <CompanyDetailsPanel
                company={fullscreenCompany}
                onClose={() => setFullscreenCompany(null)}
                darkMode={darkMode}
                onLoginRequired={handleLoginRequired}
              />
            </div>
          )}
          
          {/* Login Modal */}
          <LoginModal 
            isOpen={isLoginModalOpen}
            onClose={() => setIsLoginModalOpen(false)}
            darkMode={darkMode}
            actionType={loginModalAction}
            initialMode={loginModalMode}
          />
          
          {/* Referral Dashboard Modal */}
          {referralDashboard && (
            <div className="modal-overlay" onClick={() => setReferralDashboard(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <ReferralDashboard
                  companyId={referralDashboard.companyId}
                  companyName={referralDashboard.companyName}
                  darkMode={darkMode}
                  onClose={() => setReferralDashboard(null)}
                />
              </div>
            </div>
          )}
        </div>
      </MapProvider>
      
      {/* Dark mode toggle - Disabled for future release */}
      {/* <div className="controls">
        <button 
          className="dark-mode-toggle"
          onClick={toggleDarkMode}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <span className="material-icons">
            {darkMode ? 'light_mode' : 'dark_mode'}
          </span>
        </button>
      </div> */}
      
      {/* Comparison Cart Widget */}
      <ComparisonCartWidget 
        onOpenComparison={(type) => setComparisonModalType(type)}
        onCompanyClick={(company) => setSelectedCompany(company)}
        onJobClick={(job) => {
          // Show job details popup with all the job data
          showJobDetailsPopup(job);
        }}
      />
      
      {/* Comparison View Modal */}
      {comparisonModalType && (
        <ComparisonView 
          type={comparisonModalType}
          onClose={() => setComparisonModalType(null)}
        />
      )}
      
      {activePage === 'landing' && <Footer darkMode={darkMode} />}
        </div>
      </ComparisonProvider>
    </AuthProvider>
  );
}

// Global function for country comparison
if (typeof window !== 'undefined') {
  (window as any).compareJobWithCountry = (jobId: string, jobTitle: string, currentSalary: number) => {
    const inputElement = document.getElementById(`country-compare-${jobId}`) as HTMLInputElement;
    const resultElement = document.getElementById(`country-comparison-result-${jobId}`);
    
    if (!inputElement || !resultElement) return;
    
    const countryName = inputElement.value.trim();
    if (!countryName) {
      resultElement.innerHTML = '<div style="color: #ef4444; font-size: 12px; margin-top: 8px;">Please enter a country name</div>';
      return;
    }
    
    // Mock country salary data (in real app, this would come from API)
    const countryData: { [key: string]: { avgSalary: number; currency: string; costOfLiving: number } } = {
      'germany': { avgSalary: 65000, currency: '€', costOfLiving: 85 },
      'poland': { avgSalary: 28000, currency: '€', costOfLiving: 55 },
      'uk': { avgSalary: 55000, currency: '£', costOfLiving: 110 },
      'slovakia': { avgSalary: 30000, currency: '€', costOfLiving: 56 },
      'czech republic': { avgSalary: 32000, currency: '€', costOfLiving: 58 },
      'hungary': { avgSalary: 26000, currency: '€', costOfLiving: 52 },
      'romania': { avgSalary: 24000, currency: '€', costOfLiving: 48 },
      'netherlands': { avgSalary: 55000, currency: '€', costOfLiving: 88 },
      'sweden': { avgSalary: 58000, currency: '€', costOfLiving: 95 },
      'norway': { avgSalary: 72000, currency: '€', costOfLiving: 120 }
    };
    
    const country = countryData[countryName.toLowerCase()];
    
    if (!country) {
      resultElement.innerHTML = `
        <div style="color: #ef4444; font-size: 12px; margin-top: 8px;">
          Country not found. Try: Germany, Poland, UK, Slovakia, Czech Republic, Hungary, Romania, Netherlands, Sweden, Norway
        </div>
      `;
      return;
    }
    
    const salaryDifference = currentSalary - country.avgSalary;
    const percentageDiff = ((salaryDifference / country.avgSalary) * 100).toFixed(1);
    const adjustedSalary = (currentSalary * (country.costOfLiving / 100)).toFixed(0);
    
    resultElement.innerHTML = `
      <div style="margin-top: 12px; padding: 12px; background: #f8f9fa; border-radius: 8px; font-size: 13px;">
        <div style="font-weight: 600; margin-bottom: 8px; color: #1a1a1a;">Comparison with ${countryName}</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px;">
          <div>
            <div style="color: #6b7280; font-size: 11px;">Your Salary</div>
            <div style="font-weight: 600; color: #059669;">$${currentSalary.toLocaleString()}</div>
          </div>
          <div>
            <div style="color: #6b7280; font-size: 11px;">${countryName} Average</div>
            <div style="font-weight: 600; color: #2563eb;">${country.currency}${country.avgSalary.toLocaleString()}</div>
          </div>
        </div>
        <div style="margin-bottom: 8px;">
          <div style="color: #6b7280; font-size: 11px;">Cost of Living Adjusted</div>
          <div style="font-weight: 600; color: #7c3aed;">$${adjustedSalary}</div>
        </div>
        <div style="padding: 8px; background: ${salaryDifference > 0 ? '#d1fae5' : '#fee2e2'}; border-radius: 6px; color: ${salaryDifference > 0 ? '#065f46' : '#991b1b'}; font-size: 12px;">
          ${salaryDifference > 0 ? '📈' : '📉'} Your salary is ${Math.abs(Number(percentageDiff))}% ${salaryDifference > 0 ? 'higher' : 'lower'} than the average in ${countryName}
        </div>
      </div>
    `;
  };
}

export default App;